import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { logger, type LogContext } from '@/utils/debugLogger';

const LOG: LogContext = { component: 'appStore' };

interface AppState {
  // Loading state
  isLoading: boolean;
  loadingMessage: string | null;
  progress: number | null;
  progressDetails: string | null;

  // Notifications
  notifications: Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
    duration: number;
  }>;

  // Backend health
  backendHealthy: boolean;
  lastHealthCheck: Date | null;

  // Actions
  setLoading: (isLoading: boolean, message?: string) => void;
  setProgress: (progress: number | null, details?: string) => void;
  addNotification: (notification: Omit<AppState['notifications'][0], 'id'>) => void;
  removeNotification: (id: string) => void;
  setBackendHealth: (healthy: boolean) => void;
  checkBackendHealth: () => Promise<void>;
}

export const useAppStore = create<AppState>()(
  devtools(
    (set, get) => ({
      // Initial state
      isLoading: false,
      loadingMessage: null,
      progress: null,
      progressDetails: null,
      notifications: [],
      backendHealthy: false,
      lastHealthCheck: null,

      // Actions
      setLoading: (isLoading: boolean, message?: string) => {
        set({ isLoading, loadingMessage: message });
      },

      setProgress: (progress: number | null, details?: string) => {
        set({ progress, progressDetails: details });
      },

      addNotification: (notification) => {
        const id = Date.now().toString();
        const newNotification = { ...notification, id };
        set((state) => ({
          notifications: [...state.notifications, newNotification],
        }));

        // Auto-remove notification after duration
        if (notification.duration > 0) {
          setTimeout(() => {
            get().removeNotification(id);
          }, notification.duration);
        }
      },

      removeNotification: (id) => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      },

      setBackendHealth: (healthy) => {
        set({ backendHealthy: healthy, lastHealthCheck: new Date() });
      },

      checkBackendHealth: async () => {
        try {
          const response = await fetch('/api/health');
          const healthy = response.ok;
          get().setBackendHealth(healthy);
          
          if (!healthy) {
            get().addNotification({
              type: 'warning',
              title: 'Backend Connection',
              message: 'Backend server is not responding properly',
              duration: 5000,
            });
          }
        } catch (error) {
          logger.debug(LOG, 'Backend health check failed', error);
          get().setBackendHealth(false);
          get().addNotification({
            type: 'error',
            title: 'Connection Error',
            message: 'Unable to connect to backend server',
            duration: 5000,
          });
        }
      },
    }),
    {
      name: 'app-store',
    }
  )
);
